import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Leaf } from "lucide-react";

const F = {
  950: "#2d2219", 900: "#3a2e28", 800: "#504136",
  500: "#689689", 400: "#83E8BA",
  c100: "#f0ebe6", c50: "#faf8f5",
  m300: "#c4b8ae", m600: "#9a8a7e",
};

const Section = ({ title, children }) => (
  <section className="mb-10">
    <h2 className="text-xl font-bold mb-3" style={{ color: F.c100, fontFamily: "Chivo, sans-serif" }}>{title}</h2>
    <div className="space-y-3 text-sm leading-relaxed" style={{ color: F.m300 }}>
      {children}
    </div>
  </section>
);

export default function TermsOfService() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ background: F[950] }}>
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b px-6 py-4 flex items-center gap-4" style={{ background: F[950], borderColor: F[800] }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} style={{ color: F.m300 }}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: F[500] }}>
            <Leaf className="w-4 h-4" style={{ color: F.c50 }} />
          </div>
          <span className="font-black text-sm" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>EV Hub Studio</span>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <h1 className="text-4xl font-black mb-2" style={{ fontFamily: "Chivo, sans-serif", color: F.c100 }}>Terms of Service</h1>
        <p className="text-sm mb-10" style={{ color: F.m600 }}>Last updated: 25 April 2026</p>

        <Section title="1. Acceptance of Terms">
          <p>By accessing or using EV Hub Studio ("the Service"), you agree to be bound by these Terms of Service. If you do not agree, do not use the Service. These terms form a legally binding agreement between you and <strong style={{ color: F.c100 }}>Stuthi Energy Private Limited</strong> ("we", "us", "our").</p>
        </Section>

        <Section title="2. Description of Service">
          <p>EV Hub Studio provides AI-assisted tools for planning EV charging infrastructure, including site analysis, ROI projections, hardware recommendations, and subsidy identification. Outputs are for informational and planning purposes only and do not constitute professional engineering, legal, or financial advice.</p>
        </Section>

        <Section title="3. Eligibility">
          <p>You must be at least 18 years old and have the legal authority to enter into contracts to use the Service. By registering, you represent that you meet these requirements.</p>
        </Section>

        <Section title="4. Accounts">
          <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Notify us immediately at <a href="mailto:support@evhubstudio.com" style={{ color: F[400] }}>support@evhubstudio.com</a> if you suspect unauthorised access.</p>
          <p>We reserve the right to suspend or terminate accounts that violate these terms or are used for fraudulent purposes.</p>
        </Section>

        <Section title="5. Acceptable Use">
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Use the Service for any unlawful purpose or in violation of applicable Indian law</li>
            <li>Attempt to reverse-engineer, scrape, or systematically copy the Service</li>
            <li>Upload malicious code, viruses, or content that infringes third-party rights</li>
            <li>Use automated tools to access the Service in a manner that degrades performance for other users</li>
            <li>Impersonate any person or entity</li>
          </ul>
        </Section>

        <Section title="6. Intellectual Property">
          <p><strong style={{ color: F.c100 }}>Our IP:</strong> The EV Hub Studio platform, design, software, and all content we produce are the exclusive property of Stuthi Energy Private Limited. Nothing in these terms grants you any rights to our trademarks or software.</p>
          <p><strong style={{ color: F.c100 }}>Your data:</strong> You retain ownership of all project data and inputs you provide. By using the Service, you grant us a limited licence to process your data solely to deliver the Service.</p>
          <p><strong style={{ color: F.c100 }}>AI outputs:</strong> Reports and analyses generated by the Service are provided to you for your use. We do not claim ownership of outputs generated specifically from your project data.</p>
        </Section>

        <Section title="7. Disclaimer of Warranties">
          <p>The Service is provided "as is" and "as available" without warranties of any kind, express or implied. We do not warrant that AI-generated outputs are accurate, complete, or suitable for any specific purpose. Always verify critical infrastructure decisions with qualified professionals.</p>
        </Section>

        <Section title="8. Limitation of Liability">
          <p>To the maximum extent permitted by law, Stuthi Energy Private Limited shall not be liable for any indirect, incidental, special, or consequential damages arising from use of or inability to use the Service. Our total liability for any claim shall not exceed the amount you paid us in the 12 months preceding the claim.</p>
        </Section>

        <Section title="9. Indemnification">
          <p>You agree to indemnify and hold harmless Stuthi Energy Private Limited, its officers, employees, and partners from any claim, loss, or damage (including legal fees) arising from your use of the Service, your violation of these terms, or your violation of any third-party rights.</p>
        </Section>

        <Section title="10. Modifications to the Service">
          <p>We reserve the right to modify, suspend, or discontinue any part of the Service at any time with reasonable notice. We will not be liable to you for any such modifications.</p>
        </Section>

        <Section title="11. Governing Law and Dispute Resolution">
          <p>These terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of courts in Bengaluru, Karnataka, India. We encourage resolving disputes amicably — contact us first at <a href="mailto:legal@evhubstudio.com" style={{ color: F[400] }}>legal@evhubstudio.com</a>.</p>
        </Section>

        <Section title="12. Changes to These Terms">
          <p>We may update these terms from time to time. Material changes will be communicated via email or in-app notice at least 7 days before taking effect. Continued use of the Service after the effective date constitutes acceptance.</p>
        </Section>

        <Section title="13. Contact">
          <p>Stuthi Energy Private Limited<br />
          Email: <a href="mailto:legal@evhubstudio.com" style={{ color: F[400] }}>legal@evhubstudio.com</a></p>
        </Section>
      </main>

      <footer className="text-center py-8 text-xs border-t" style={{ color: F.m600, borderColor: F[800] }}>
        © {new Date().getFullYear()} Stuthi Energy Private Limited
      </footer>
    </div>
  );
}
